import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RevealGroup, RevealItem } from "@/components/Reveal";
import { useAssessment } from "@/context/AssessmentContext";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { packages } from "@/data/content";
import { cn } from "@/lib/utils";

export default function PackagesGrid() {
  const { openAssessment } = useAssessment();
  const onChoose = (name) => {
    trackEvent(EVENTS.PACKAGE_INQUIRY, { package: name });
    openAssessment();
  };

  return (
    <RevealGroup className="grid gap-6 lg:grid-cols-4" stagger={0.08}>
      {packages.map((p) => {
        const featured = p.recommended;
        return (
          <RevealItem key={p.name}>
            <div
              data-testid={`package-card-${p.name.toLowerCase()}`}
              className={cn(
                "relative flex h-full flex-col rounded-2xl border p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-2",
                featured
                  ? "border-ocean bg-navy text-white shadow-float-lg"
                  : "border-line bg-white text-navy shadow-float hover:shadow-float-lg"
              )}
            >
              {featured && (
                <span className="absolute -top-3 left-7 rounded-full bg-sky px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-navy" data-testid="package-recommended-badge">
                  Recommended
                </span>
              )}
              <h3 className={cn("font-serif text-2xl font-bold", featured ? "text-white" : "text-navy")}>{p.name}</h3>
              <p className={cn("mt-1 text-2xl font-semibold", featured ? "text-sky" : "text-ocean")}>Custom Pricing</p>
              <p className={cn("mt-4 text-sm leading-relaxed", featured ? "text-white/70" : "text-slate-600")}>{p.tagline}</p>

              <ul className="mt-6 flex-1 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <Check className={cn("mt-0.5 h-4 w-4 shrink-0", featured ? "text-sky" : "text-ocean")} />
                    <span className={featured ? "text-white/85" : "text-slate-700"}>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => onChoose(p.name)}
                data-testid={`package-cta-${p.name.toLowerCase()}`}
                className={cn(
                  "mt-8 w-full rounded-full py-6 text-sm font-semibold",
                  featured ? "bg-sky text-navy hover:bg-sky-light" : "bg-navy text-white hover:bg-navy-800"
                )}
              >
                {p.cta}
              </Button>
            </div>
          </RevealItem>
        );
      })}
    </RevealGroup>
  );
}

export const PackagesNote = () => {
  const { openAssessment } = useAssessment();
  return (
    <div className="mt-12 rounded-2xl border border-line bg-mist p-8 text-center">
      <p className="text-sm text-slate-600">
        Packages can be customized according to property size, number of rooms, number of platforms and services required.
      </p>
      <p className="mt-4 font-serif text-lg text-navy">Need something different?</p>
      <Button onClick={openAssessment} data-testid="custom-package-cta" className="mt-4 rounded-full bg-ocean px-8 text-white hover:bg-royal">
        Request a Custom Package
      </Button>
    </div>
  );
};
